/* eslint-disable no-unused-vars */

import _ from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

import TasksSelector from './tasks/tasks.selector';
import InspectionSelector from './inspection/inspection.selector';
import { getTaskAssignedUserTags, getTaskHeaderTitle, TYPE_ICONS } from './tasks/helper';
import { getDataPoints, getMoreInfoDataPoints } from '../pages/settings/helper';
import CheckSelector from './checks/check.selector';
import {
  filterEquipmentAssetBasedOnMocHseStructureNcrPipeline,
  getTaskEquipmentAsset
} from './global/equipment';
import { getTaskCardFooter } from './global/footer';
import { getTaskSiteAsset } from './global/site';
import EquipmentAutoPopulate from '@components/work-order/services/equipment-auto-populate';
import structureAutoPopulate from '@components/work-order/services/structure-auto-populate';
import { getMoreInfoFormFields } from '@components/add-edit-equipment/form-fields';
import { dropdownMenuMap } from '@utilities/helper.util';

const EquipmentSelector = {
  getEquipmentsWithAllTasks: async ({
    parentEquipment = [],
    id = '',
    childrenMapIds = false,
    withChild = true,
    templatesIds = '',
    structureId = null,
    equipmentCategories = [],
    showAttachedPSVs = false,
    formWrapper = false,
    store,
    getWorkRequestData = false,
    createSiteDataPoints = false,
    psvEquipments = false,
    alreadyAttachedPSV = []
  }) => {
    const { tasks, cache, auth, jurisdictionNumbers, settings, picklists, pvPsv, inspection } =
      store;
    const { workOrderUsersMap, workOrderUsersGroupsMap } = tasks;
    const userId = auth.userId || '';
    const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;
    const equipmentDisplayFields = settings.settings?.['equipmentDisplayFields'] || '';
    const equipmentHiddenDisplayFields = settings.hiddenDataPointsForAssets?.equipment || '';
    const { picklistsMap = {} } = picklists;

    const response = !formWrapper
      ? await EquipmentSelector?.getAllWorkOrdersAndInspections({
          templatesIds,
          store,
          getWorkRequestData,
          recordsCount: true
        })
      : {};

    const allTasksList = response?.assignedTasksList || [];
    const newAllWorkRequestList = response?.newWorkRequestList || [];
    const onDemandRecordList = response?.onDemandRecordList || [];

    let searchingEquipmentDataPoints = equipmentHiddenDisplayFields;

    return new Promise((resolve) => {
      const responsibleUsersMap = {
        ...(workOrderUsersMap || {}),
        ...(workOrderUsersGroupsMap || {})
      };

      const {
        equipmentsMap = {},
        sitesEquipmentsMap = {},
        equipments: cacheEquipments = [],
        sites: cacheSites = [],
        sitesMap = {},
        syncedNewAndEditedEquipments = []
      } = cache;

      const syncedNewAndEditedEquipmentsMap = syncedNewAndEditedEquipments?.reduce((acc, curr) => {
        if (!acc[curr.equipmentId]) {
          acc[curr.equipmentId] = 1;
        } else {
          acc[curr.equipmentId] = acc[curr.equipmentId] + 1;
        }
        return acc;
      }, {});

      let _equipmentCategories = psvEquipments
        ? inspection?.equipmentCategory
            ?.filter((x) => x?.allowedForPSVCreation)
            ?.map((y) => y?.value) || []
        : equipmentCategories;

      let equipmentsList =
        _.size(parentEquipment) > 0
          ? parentEquipment
          : !_.isEmpty(id)
          ? cacheEquipments?.filter((x) => x.equipmentId == id)
          : structureId
          ? cacheEquipments?.filter((x) => x?.structureId === structureId)
          : _.size(_equipmentCategories) > 0
          ? cacheEquipments?.filter((item) =>
              new Set(_equipmentCategories)?.has(item.equipmentCategory)
            )
          : childrenMapIds
          ? cacheEquipments?.filter((x) => childrenMapIds[x.equipmentId])
          : cacheEquipments;

      equipmentsList =
        psvEquipments && alreadyAttachedPSV?.length > 0
          ? equipmentsList?.filter((x) => !alreadyAttachedPSV?.includes(x?.equipmentId))
          : equipmentsList;

      let prepareEquipments = [];

      equipmentsList.map((equipment) => {
        const { children = [] } = equipment;
        const parentEquipmentChildren = children?.map((childIndex) => {
          return { ...cacheEquipments[childIndex], children: [] };
        });

        let childrenData = [];
        if (equipment?.children?.length) {
          childrenData = EquipmentSelector.attachMeta(
            parentEquipmentChildren,
            sitesEquipmentsMap,
            equipmentsMap,
            allTasksList,
            cacheEquipments
          );
        }

        let allTasksForSite = [
          ...allTasksList.filter(
            (t) =>
              t?.equipmentId === equipment?.equipmentId ||
              t?.relatedAssetId === equipment?.equipmentId ||
              t?.equipments?.includes(equipment?.equipmentId)
          ),
          ...(withChild ? childrenData?.map((t) => t.allTasksForSite).flat(2) || [] : [])
        ];

        let newWorkRequestList = [
          ...newAllWorkRequestList.filter(
            (t) =>
              t?.equipmentId === equipment?.equipmentId ||
              t?.relatedAssetId === equipment?.equipmentId ||
              t?.equipments?.includes(equipment?.equipmentId)
          )
        ];

        const allOnDemandRecordCount = onDemandRecordList?.filter(
          (t) =>
            t?.equipmentId === equipment?.equipmentId ||
            t?.relatedAssetId === equipment?.equipmentId ||
            t?.primaryAssetId === equipment?.equipmentId ||
            t?.equipments?.includes(equipment?.equipmentId) ||
            parentEquipmentChildren?.some((x) => x?.equipmentId === t?.equipmentId && withChild)
          // ||
          // t?.siteId === equipment?.siteId ||
          // t?.relatedAssetId === equipment?.siteId ||
          // t?.primaryAssetId === equipment?.siteId ||
          // t?.sites?.includes(equipment?.siteId)
          // ||
          // parentEquipmentChildren?.some((x) => x?.siteId === t?.siteId)
        );

        allTasksForSite = allTasksForSite.map((tas) => {
          const { responsibleUsers = [], responsibleUserGroups = [] } = tas;
          const customAssignedTaskValue = getTaskAssignedUserTags({
            responsibleUsers,
            responsibleUserGroups,
            workOrderUsersGroupsMap,
            userId,
            responsibleUsersMap
          });

          return {
            customAssignedTaskValue,
            ...tas
          };
        });

        const allDoneTasks = allTasksForSite.filter((task) => task?.status === 'Synced');
        const allotDoneTasks = allTasksForSite.filter((task) => task?.status !== 'Synced');

        const today = moment().format('YYYY-MM-DD');
        const isOverDue = allotDoneTasks.find((task) =>
          task.dueDate ? moment(task.dueDate).isBefore(today) : false
        );

        const { jurisdictionNumber } = jurisdictionNumbersMap[equipment?.equipmentId] || {};

        const dataPoints = getDataPoints(
          { ...equipment, subtype: equipment?.equipmentSubType, jurisdictionNumber },
          equipmentDisplayFields,
          'equipment',
          picklistsMap
        );

        const dataPointsForSearch = searchingEquipmentDataPoints
          ? getDataPoints(
              { ...equipment, subtype: equipment?.equipmentSubType, jurisdictionNumber },
              searchingEquipmentDataPoints,
              'equipment',
              picklistsMap
            )
          : [];

        const equipmentChildren = !formWrapper
          ? equipment?.children?.map((childIndex) => {
              return { ...cacheEquipments[childIndex], children: [] };
            })
          : [];
        let equipmentPSVs = [];
        if (showAttachedPSVs && id) {
          const { pvPsvMap = {} } = pvPsv;
          const { psvsData = [] } = getEquipmentAttachedPSV({
            equipmentId: id,
            pvPsvMap,
            cacheEquipments,
            equipmentsMap,
            jurisdictionNumbersMap,
            equipmentDisplayFields,
            picklistsMap
          });
          equipmentPSVs = psvsData;
        }

        // create site dataPoints for searching

        let dataPointsForSite = [],
          siteDataPointsForSearch = [];

        if (createSiteDataPoints) {
          const site = equipment?.siteId ? cacheSites[sitesMap[equipment.siteId]] || {} : {};

          if (site) {
            const siteHiddenDisplayFields = settings.hiddenDataPointsForAssets?.site || '';
            const siteDisplayFields = settings.settings?.siteDisplayFields;

            dataPointsForSite = getDataPoints(
              { ...site, type: site.siteType },
              siteDisplayFields,
              'site',
              picklistsMap
            );

            siteDataPointsForSearch = siteHiddenDisplayFields
              ? getDataPoints(
                  { ...site, type: site?.siteType },
                  siteHiddenDisplayFields,
                  'site',
                  picklistsMap
                )
              : [];
          }
        }

        let onDemandEquipmentCount = syncedNewAndEditedEquipmentsMap[equipment.equipmentId] || 0;

        onDemandEquipmentCount =
          equipment?.equipmentEditingStatus &&
          ['In progress', 'Pending sync'].includes(equipment.equipmentEditingStatus)
            ? onDemandEquipmentCount + 1
            : onDemandEquipmentCount;

        let childCount = 0;
        if (equipmentChildren?.length > 0 && withChild) {
          for (let childEqu of equipmentChildren) {
            let onDemandEquipmentCountChild =
              syncedNewAndEditedEquipmentsMap[childEqu.equipmentId] || 0;

            onDemandEquipmentCountChild =
              childEqu?.equipmentEditingStatus &&
              ['In progress', 'Pending sync'].includes(childEqu.equipmentEditingStatus)
                ? onDemandEquipmentCountChild + 1
                : onDemandEquipmentCountChild;

            childCount += onDemandEquipmentCountChild;
          }
        }
        onDemandEquipmentCount += childCount;

        if (!equipment?.notSHhowOnList) {
          prepareEquipments.push({
            ...equipment,
            status: !formWrapper ? equipment?.status : null,
            equipmentPSVs,
            children: equipmentChildren,
            allDoneTasks,
            isOverDue,
            allTasksForSite,
            allotDoneTasks,
            dataPoints,
            dataPointsForSearch,
            soonestDueDate: _.min(
              allotDoneTasks.map((t) => t?.due || t?.dueDate).map((date) => new Date(date))
            ),
            newWorkRequestList,
            allOnDemandRecordCount: allOnDemandRecordCount?.length || 0,
            onDemandEquipmentCount,

            dataPointsForSite,
            siteDataPointsForSearch
          });
        }
      });
      resolve(prepareEquipments);
    });
  },
  getSingleEquipmentDetails: async ({
    equipmentId = '',
    store,
    getWorkRequestData = false,
    firstName,
    lastName,
    isParentEquipment = false
  }) => {
    const { cache, picklists, jurisdictionNumbers, pvPsv, settings, inspection, structure } = store;
    const {
      sitesMap = {},
      equipmentsMap = {},
      equipmentsLoading = false,
      equipments: cacheEquipments = [],
      sites: cacheSites = []
    } = cache;
    const equipmentDisplayFields = settings.settings?.['equipmentDisplayFields'] || '';

    const {
      equipmentFormsUserCache = [],
      equipmentFormsUserCacheMap = {},
      equipmentCategoryTypeSubtype = []
    } = inspection;

    const response = await EquipmentSelector?.getAllWorkOrdersAndInspections({
      equipmentId,
      store,
      getWorkRequestData,
      recordsCount: true
    });

    const tasksList = response?.assignedTasksList || [];
    const newWorkRequestList = response?.newWorkRequestList || [];
    const newWorkOrderList = response?.newWorkOrderList || [];
    const onDemandRecordList = response?.onDemandRecordList || [];
    const parentEquipment = cacheEquipments[equipmentsMap[equipmentId]] || {};
    let equipmentAutoPopulateFields = EquipmentAutoPopulate({
      intervalId: null,
      firstName,
      lastName,
      equipment: parentEquipment,
      jurisdictionNumber: null,
      jurisdiction: null,
      inspectionLastDate: []
    });

    const structureId = parentEquipment?.structureId;

    const { structureMobileMap = {}, structureMobile = [] } = structure;
    const checkSheetStructureData =
      (structureId && structureMobile[structureMobileMap[structureId]]) || {};

    const structureAutoPopulateFields = structureAutoPopulate({
      intervalId: null,
      firstName,
      lastName,
      structure: checkSheetStructureData
    });

    equipmentAutoPopulateFields = {
      Equipment: {
        ...equipmentAutoPopulateFields['Equipment']
      },
      Structure: {
        ...structureAutoPopulateFields['Structure']
      }
    };

    const otherCategories = getMoreInfoDataPoints1({
      equipmentFormsUserCache,
      equipmentFormsUserCacheMap,
      equipmentDetails: parentEquipment,
      picklists,
      equipmentCategoryTypeSubtype,
      structure,
      equipmentAutoPopulateFields,
      inspection
    });

    const parentEquipmentChildren = isParentEquipment
      ? parentEquipment?.children?.map((childIndex) => {
          return { ...cache.equipments[childIndex], children: [] };
        })
      : [];

    const children = [...(parentEquipmentChildren || [])].sort(
      (a, b) =>
        (cacheSites[sitesMap[a?.siteId]]?.distance != null
          ? cacheSites[sitesMap[a?.siteId]]?.distance
          : Infinity) -
          (cacheSites[sitesMap[b?.siteId]]?.distance != null
            ? cacheSites[sitesMap[b?.siteId]]?.distance
            : Infinity) || a.name.localeCompare(b.name)
    );

    const childrenMapIds =
      children &&
      children?.reduce((acc, curr) => {
        acc[curr.equipmentId] = true;
        return acc;
      }, {});
    let childrenDetails =
      _.size(childrenMapIds) > 0
        ? await EquipmentSelector.getEquipmentsWithAllTasks({
            childrenMapIds,
            store,
            getWorkRequestData
          })
        : [];

    let equipmentIds =
      parentEquipmentChildren?.length > 0
        ? parentEquipmentChildren?.map((x) => x?.equipmentId)
        : [];
    equipmentIds = [...equipmentIds, equipmentId];
    const allEditEquipmentAssets =
      equipmentIds?.length > 0
        ? await EquipmentSelector.getAllEquipmentsThatIsEditedAndNewCreates({
            store,
            equipmentIds: equipmentIds
          })
        : [];

    return new Promise((resolve) => {
      const { picklistsMap = {} } = picklists;
      const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;
      const { pvPsvMap = {} } = pvPsv;

      const { name, siteId, equipmentType, equipmentSubType, equipmentCategory, link } =
        parentEquipment;
      const { pv = [], psv = [] } = pvPsvMap[equipmentId] || {};

      const pvs = pv
        .filter((id) => cacheEquipments?.[equipmentsMap?.[id]])
        .map((x) => cacheEquipments[equipmentsMap[x]]);

      const { jurisdiction, jurisdictionNumber } = jurisdictionNumbersMap[equipmentId] || {};
      const jurisdictionProvince = picklistsMap[jurisdiction]?.value;
      const type = picklistsMap[equipmentType]?.value;
      const category = picklistsMap[equipmentCategory]?.value;
      const status = picklistsMap[parentEquipment.status]?.value;
      const data = {
        ...parentEquipment,
        category: equipmentCategory,
        subtype: equipmentSubType,
        jurisdictionNumber
      };

      const moreInfoDataPoints = getMoreInfoDataPoints(
        { ...data, subtype: data?.equipmentSubType, jurisdictionNumber },
        'equipment',
        picklistsMap
      );

      const moreInfoDataPointsNew = {
        ...(data?.equipmentNumber
          ? {
              'Equipment #': {
                Id: 'Equipment #',
                key: 'equipmentNumber',
                value: data.equipmentNumber,
                title: 'Equipment #'
              }
            }
          : {}),

        ...(data?.name
          ? {
              Name: {
                Id: 'Name',
                key: 'name',
                value: data.name,
                title: 'Name'
              }
            }
          : {}),

        ...(picklistsMap[data?.status]?.value
          ? {
              Status: {
                Id: 'Status',
                key: 'status',
                value: picklistsMap[data.status].value,
                title: 'Status'
              }
            }
          : {}),

        ...(picklistsMap[data?.equipmentCategory]?.value
          ? {
              Category: {
                Id: 'Category',
                key: 'equipmentCategory',
                value: picklistsMap[data.equipmentCategory].value,
                title: 'Category'
              }
            }
          : {}),

        ...(picklistsMap[data?.equipmentType]?.value
          ? {
              Type: {
                Id: 'Type',
                key: 'equipmentType',
                value: picklistsMap[data.equipmentType].value,
                title: 'Type'
              }
            }
          : {}),

        ...(picklistsMap[data?.equipmentSubType]?.value
          ? {
              'Sub type': {
                Id: 'Sub type',
                key: 'equipmentSubType',
                value: picklistsMap[data.equipmentSubType].value,
                title: 'Sub type'
              }
            }
          : {}),

        ...(data?.description
          ? {
              Description: {
                Id: 'Description',
                key: 'description',
                value: data.description,
                title: 'Description'
              }
            }
          : {}),

        ...(data?.manufacturer
          ? {
              Manufacturer: {
                Id: 'Manufacturer',
                key: 'manufacturer',
                value: data.manufacturer,
                title: 'Manufacturer'
              }
            }
          : {}),

        ...(data?.model
          ? {
              Model: {
                Id: 'Model',
                key: 'model',
                value: data.model,
                title: 'Model'
              }
            }
          : {}),

        ...(data?.physicalLocation
          ? {
              'Physical location': {
                Id: 'Physical location',
                key: 'physicalLocation',
                value: data.physicalLocation,
                title: 'Physical location'
              }
            }
          : {}),

        ...(data?.serialNumber
          ? {
              'Serial #': {
                Id: 'Serial #',
                key: 'serialNumber',
                value: data.serialNumber,
                title: 'Serial #'
              }
            }
          : {}),

        ...(jurisdiction || jurisdictionNumber
          ? {
              Jurisdiction: {
                Id: 'Jurisdiction',
                key: 'jurisdiction',
                value: {
                  jurisdiction: picklistsMap[jurisdiction]?.value || null,
                  jurisdictionNumber: jurisdictionNumber || null
                },
                title: 'Jurisdiction - jurisdictionNumber'
              }
            }
          : {}),

        ...(data?.fieldTagNumber
          ? {
              'Field tag #': {
                Id: 'Field tag #',
                key: 'fieldTagNumber',
                value: data.fieldTagNumber,
                title: 'Field tag #'
              }
            }
          : {}),

        ...(data?.manufacturedYear
          ? {
              'Year built': {
                Id: 'Year built',
                key: 'manufacturedYear',
                value: data.manufacturedYear,
                title: 'Year built'
              }
            }
          : {})
      };

      const moreInfoDataPointsMap = moreInfoDataPoints.reduce((acc, curr) => {
        acc[curr.Id] = curr;
        return acc;
      }, {});
      moreInfoDataPointsMap['Type'] = { value: type, title: 'Type', key: 'type' };
      moreInfoDataPointsMap['Status'] = { value: status, title: 'Status', key: 'status' };
      if (moreInfoDataPointsMap['JurisdictionNumber'])
        moreInfoDataPointsMap['JurisdictionNumber'].before = {
          heading: 'Jurisdiction',
          title: jurisdictionProvince
        };

      const dataPoints = getDataPoints(
        { ...data, subtype: data?.equipmentSubType, jurisdictionNumber },
        equipmentDisplayFields,
        'equipment',
        picklistsMap
      );

      const dataPointsMap = dataPoints.reduce((acc, curr) => {
        acc[curr.Id] = curr;
        return acc;
      }, {});
      dataPointsMap['Type'] = { value: type, title: 'Type' };
      dataPointsMap['Status'] = { value: status, title: 'Status' };
      if (dataPointsMap['JurisdictionNumber'])
        dataPointsMap['JurisdictionNumber'].before = {
          heading: 'Jurisdiction',
          title: jurisdictionProvince
        };

      const tasks =
        _.flattenDeep([
          ...tasksList,
          ...(childrenDetails
            ?.map((t) => t.allTasksForSite)
            .flat(2)
            .filter((y) => y) || [])
        ]) || [];

      const newTasks =
        _.flattenDeep([
          ...newWorkRequestList,
          ...(childrenDetails
            ?.map((t) => t.newWorkRequestList)
            .flat(2)
            .filter((y) => y) || [])
        ]) || [];

      const newWorkOrderListWithChildWorkOrderList =
        _.flattenDeep([
          ...newWorkOrderList,
          ...(childrenDetails
            ?.map((t) => t.newWorkOrderList)
            .flat(2)
            .filter((y) => y) || [])
        ]) || [];

      const newOnDemandRecordList =
        _.flattenDeep([
          ...onDemandRecordList,
          ...(childrenDetails
            ?.map((t) => t.allOnDemandRecordCount)
            .flat(2)
            .filter((y) => y) || [])
        ]) || [];

      const allDoneTasks = tasks.filter((task) => task?.status === 'Synced');
      const allNotDoneTasks = tasks.filter((task) => task?.status !== 'Synced');

      const allInspections = tasks.filter((task) => task?.type === 'Inspection');
      const allDoneInspections = allInspections.filter((task) => task?.status === 'Synced');
      const allNotDoneInspections = allInspections.filter((task) => task?.status !== 'Synced');

      const allChecks = tasks.filter((task) => task?.type === 'Check');
      const allDoneChecks = allChecks.filter((task) => task?.status === 'Synced');
      const allNotDoneChecks = allChecks.filter((task) => task?.status !== 'Synced');

      const allWorkOrders = tasks.filter((task) => task?.type === 'Work Order');
      const allDoneWorkOrders = allWorkOrders.filter((task) => task?.status === 'Synced');
      const allNotDoneWorkOrders = allWorkOrders.filter((task) => task?.status !== 'Synced');

      const today = moment().format('YYYY-MM-DD');
      const isOverDue = allNotDoneTasks?.find((task) =>
        task?.dueDate ? moment(task?.dueDate).isBefore(today) : false
      );
      const isInspectionOverDue = allNotDoneInspections?.find((task) =>
        task?.dueDate ? moment(task?.dueDate).isBefore(today) : false
      );
      const isCheckOverDue = allNotDoneChecks?.find((task) =>
        task?.nextDue ? moment(task?.nextDue).isBefore(today) : false
      );
      const isWorkOrderOverDue = allNotDoneWorkOrders?.find((task) =>
        task?.dueDate ? moment(task?.dueDate).isBefore(today) : false
      );

      const { psvsData } = getEquipmentAttachedPSV({
        equipmentId,
        pvPsvMap,
        cacheEquipments,
        equipmentsMap,
        jurisdictionNumbersMap,
        equipmentDisplayFields,
        picklistsMap
      });
      const psvData = pvs
        ?.filter((y) => y?.equipmentId)
        ?.map((x) => {
          const { jurisdictionNumber } = jurisdictionNumbersMap[x?.equipmentId] || {};
          const data = {
            ...x,
            category: x?.equipmentCategory,
            subtype: x?.equipmentSubType,
            jurisdictionNumber
          };

          const dataPoints = getDataPoints(
            { ...data, subtype: data?.equipmentSubType, jurisdictionNumber },
            equipmentDisplayFields,
            'equipment',
            picklistsMap
          );
          return {
            ...x,
            dataPoints
          };
        });

      const moreInfoOfVessel =
        pvs?.length > 0
          ? pvs?.map((a) => {
              const category = picklistsMap[a?.equipmentCategory]?.value || null;
              const type = picklistsMap[a?.equipmentType]?.value || null;
              return {
                id: a?.equipmentId,
                title: `${category} - ${type}`,
                name: a?.name || null,
                serial: a?.serialNumber || null,
                link: a?.link || null
              };
            })
          : [];

      const moreInfoOfPSVs =
        psvsData?.length > 0
          ? psvsData?.map((a) => {
              const category = picklistsMap[a?.equipmentCategory]?.value || null;
              const type = picklistsMap[a?.equipmentType]?.value || null;
              return {
                id: a?.equipmentId,
                title: `${category} - ${type}`,
                name: a?.name || null,
                serial: a?.serialNumber || null,
                link: a?.link || null
              };
            })
          : [];

      const newOrEditedEquipmentOnDemandCount = allEditEquipmentAssets?.length || 0;

      resolve({
        equipmentsLoading,
        parentEquipment,
        tasks,
        allDoneTasks,
        allInspections,
        allDoneInspections,
        allWorkOrders,
        allDoneWorkOrders,
        allChecks,
        allDoneChecks,
        isCheckOverDue,
        isInspectionOverDue,
        isWorkOrderOverDue,
        isOverDue,
        children: childrenDetails,
        psv,
        psvs: psvsData,
        // equipmentsMap,
        pvs: psvData,
        dataPoints,
        category,
        type,
        // sitesMap,
        siteId,
        dataPointsMap,
        moreInfoDataPointsMap,
        data,
        status,
        name,
        // cacheEquipments,
        // cacheSites,
        link,
        newWorkRequestList: newTasks,
        allOnDemandRecordCount: newOnDemandRecordList?.length || 0,
        newWorkOrderList: newWorkOrderListWithChildWorkOrderList,
        newChecksList: [],
        newInspectionList: [],
        moreInfoDataPointsNew,
        moreInfoOfVessel,
        moreInfoOfPSVs,
        allEditEquipmentAssets,
        newOrEditedEquipmentOnDemandCount,
        otherCategories
      });
    });
  },
  attachMeta: (
    equipmentList,
    sitesEquipmentsMap,
    equipmentsMap,
    allWOTasks,
    cacheEquipments = []
  ) => {
    let prepareSites = [];
    equipmentList.map((equipment) => {
      const allTasksForSite = allWOTasks.filter(
        (t) =>
          t?.equipmentId === equipment?.equipmentId ||
          t?.equipments?.includes(equipment?.equipmentId)
      );
      const allDoneTasks = allTasksForSite.filter((task) => task?.status === 'Synced');
      const allotDoneTasks = allTasksForSite.filter((task) => task?.status !== 'Synced');

      const today = moment().format('YYYY-MM-DD');
      const isOverDue = allotDoneTasks.find((task) =>
        task.dueDate ? moment(task.dueDate).isBefore(today) : false
      );

      prepareSites.push({
        allDoneTasks,
        isOverDue,
        allTasksForSite,
        allotDoneTasks,
        soonestDueDate: _.min(
          allotDoneTasks.map((t) => t?.due || t?.dueDate).map((date) => new Date(date))
        )
      });
    });

    return prepareSites;
  },
  getAllWorkOrdersAndInspections: async ({
    templatesIds = null,
    equipmentId = null,
    store,
    getWorkRequestData = false,
    recordsCount = false
  }) => {
    const { inspection: storeInspection, checks: storeChecks, auth } = store;
    const { userId } = auth;

    const [tasks, inspections, checks] = await Promise.all([
      await TasksSelector.getTasks({ equipmentId, store }),
      await InspectionSelector.getInspections({ equipmentId, store }),
      await CheckSelector.getAllChecks({ equipmentId, store })
    ]);

    const allAssignedWorkOrders = _.isEmpty(templatesIds)
      ? tasks?.filter((task) =>
          task.newWorkOrder && task?.status !== 'To do'
            ? task?.responsible?.[0]?.woUserRequestId === userId
              ? true
              : false
            : task?.status === 'Synced'
            ? !task?.isNew
              ? true
              : false
            : !task.newWorkRequest
        )
      : tasks?.filter((task) =>
          task.newWorkOrder && task?.status !== 'To do'
            ? task?.responsible?.[0]?.woUserRequestId === userId
              ? true
              : false
            : task?.status === 'Synced'
            ? !task?.isNew
              ? true
              : false
            : !task.newWorkRequest && templatesIds?.includes(task?.rtNumber)
        );

    const allAssignedInspections = inspections;
    const AllAssignedChecks = checks;

    const newWorkRequestList = _.isEmpty(templatesIds)
      ? tasks?.filter((task) => task.newWorkRequest || task?.entityType === 'WorkRequest')
      : tasks?.filter(
          (task) =>
            (task.newWorkRequest || task?.entityType === 'WorkRequest') &&
            templatesIds?.includes(task?.rtNumber)
        );

    const newWorkOrderList = _.isEmpty(templatesIds)
      ? tasks?.filter((task) =>
          (task.newWorkOrder || task?.entityType === 'WorkOrder') &&
          task?.responsible?.[0]?.woUserRequestId !== userId &&
          task?.status !== 'To do' &&
          task?.status !== 'Synced'
            ? true
            : task?.status === 'Synced' && task?.isNew && task?.entityType !== 'WorkRequest'
            ? true
            : false
        )
      : tasks?.filter((task) =>
          (task.newWorkOrder || task?.entityType === 'WorkOrder') &&
          task?.responsible?.[0]?.woUserRequestId !== userId &&
          task?.status !== 'To do' &&
          task?.status !== 'Synced'
            ? true
            : task?.status === 'Synced' && task?.isNew && task?.entityType !== 'WorkRequest'
            ? true
            : templatesIds?.includes(task?.rtNumber)
        );

    let onDemandRecordList = [];

    onDemandRecordList = [...newWorkRequestList, ...newWorkOrderList];

    return {
      assignedTasksList: [
        ...allAssignedWorkOrders,
        ...allAssignedInspections,
        ...AllAssignedChecks
      ],
      newWorkRequestList,
      newWorkOrderList,
      onDemandRecordList
    };
  },
  getAllEquipmentsThatIsEditedAndNewCreates: async ({ store, equipmentIds = [] }) => {
    const { cache, auth, jurisdictionNumbers, settings, picklists } = store;
    const { picklistsMap = {}, areaHierarchyTreeDataMap = {} } = picklists;

    return new Promise((resolve) => {
      const {
        equipmentsMap = {},
        equipments: cacheEquipments = [],
        equipmentsWithoutMobileMap = {},
        sites: cacheSites = [],
        sitesMap = {},
        syncedNewAndEditedEquipments = []
      } = cache;

      const { equipmentDisplayFields, siteDisplayFields } = settings.settings;
      const { jurisdictionNumbersMap } = jurisdictionNumbers;

      let equipmentList =
        equipmentIds?.length > 0
          ? cacheEquipments?.filter((x) => equipmentIds.includes(x.equipmentId)) || []
          : cacheEquipments;
      equipmentList =
        equipmentList?.filter((x) => x?.equipmentEditingStatus && !x?.notSHhowOnList) || [];

      let syncList =
        equipmentIds?.length > 0
          ? syncedNewAndEditedEquipments?.filter((x) => equipmentIds.includes(x.equipmentId)) || []
          : syncedNewAndEditedEquipments;

      const syncedEquipmentLists =
        syncList?.map((x) => {
          const value = cacheEquipments[equipmentsMap[x.equipmentId]] || {};
          return {
            ...value,
            ...x
          };
        }) || [];

      equipmentList = [...equipmentList, ...syncedEquipmentLists];
      const equipmentHiddenDisplayFields = settings.hiddenDataPointsForAssets?.equipment || '';
      const siteHiddenDisplayFields = settings.hiddenDataPointsForAssets?.site || '';

      const userId = auth.userId || '';

      const prepareEquipments = [];
      for (let equipment of equipmentList) {
        const taskHeaderIconName = TYPE_ICONS['New Asset'] || '';

        const type = picklistsMap?.[equipment?.typeId]?.value || '';
        const category = picklistsMap?.[equipment?.category]?.value || '';

        const taskCardHeaderTitle =
          getTaskHeaderTitle({
            status: equipment?.equipmentEditingStatus || '',
            type: 'equipment',
            isNew: false,
            id: equipment?.equipmentId || '',
            entity: '',
            isNewEquipment: equipment?.isNew ? true : false,
            reconciliationNumber: equipment?.reconciliationNumber || ''
          }) || '';

        const taskFooter = getTaskCardFooter({
          status: equipment?.equipmentEditingStatus || null,
          sync: false,
          dueDate: null,
          startDate: null,
          due: null,
          syncedByText: equipment?.syncedByText || null,
          syncedDate: equipment?.createdDate || null,
          syncedBy: null,
          updatedAt: equipment?.updatedAt || new Date(),
          syncDateCustom: equipment?.syncDateCustom || null,
          userId,
          editAsset: true
        });

        let siteAsset = null;
        if (equipment?.isNew && !equipment?.equipmentId) {
          siteAsset = getTaskSiteAsset({
            sitesMap,
            siteId: equipment?.siteId || null,
            sites: equipment?.sites || [],
            areaHierarchyTreeDataMap,
            siteDisplayFields,
            siteHiddenDisplayFields,
            picklistsMap,
            removeConfigurableFieldFromAsset: false,
            cacheSites,
            appliesTo: false,
            formWrapper: false,
            store
          });
        }

        const equipmentAsset =
          equipment?.isNew && !equipment?.equipmentId
            ? {
                equipmentsIds: [uuidv4()],
                prepareTasksEquipments: [
                  {
                    // title: 'PSV - PSV',
                    // subtitle: 'Plant 1 Inlet Seperator PSV',
                    title: `${category} - ${type}`,
                    subtitle: equipment?.name || null,
                    equipmentId: uuidv4()
                  }
                ]
              }
            : getTaskEquipmentAsset({
                equipmentsWithoutMobileMap,
                equipmentsMap,
                cacheEquipments,
                equipments: [],
                equipmentId: equipment.equipmentId || '',
                picklistsMap,
                equipmentDisplayFields,
                equipmentHiddenDisplayFields,
                jurisdictionNumbersMap,
                sitesMap,
                siteDisplayFields,
                siteHiddenDisplayFields,
                areaHierarchyTreeDataMap,
                removeConfigurableFieldFromAsset: false,
                cacheSites,
                appliesTo: false,
                showAttachedPSVs: false,
                formWrapper: false,
                store
              });

        const equipmentAssetFilterData = equipmentAsset;

        const dataPoints = equipment?.statusText
          ? [
              {
                Id: 'Status',
                key: 'status',
                value: 'To do',
                title: 'Status'
              }
            ]
          : [];

        prepareEquipments.push({
          ...equipment,
          taskHeaderIconName,
          taskCardHeaderTitle,
          taskFooter,
          status: equipment?.equipmentEditingStatus,
          equipmentSelectedStatus: equipment?.status,
          equipmentAsset,
          equipmentAssetFilterData,
          link: equipment?.link || '',
          checked: false,
          equipmentRecord: true,
          id: equipment?.id || uuidv4(),
          type: 'New or edited asset',
          siteAsset,
          sitesAssetFilterData: siteAsset,
          dataPoints,
          taskCardWorkStatus: equipment?.statusText || '',
          syncedDate: equipment?.createdDate || null
        });
      }
      resolve(prepareEquipments);
    });
  }
};

export default EquipmentSelector;

export const getEquipmentAttachedPSV = ({
  equipmentId,
  pvPsvMap,
  cacheEquipments,
  equipmentsMap,
  jurisdictionNumbersMap,
  equipmentDisplayFields,
  picklistsMap
}) => {
  const { psv = [] } = pvPsvMap[equipmentId] || {};
  const psvs = psv
    .filter((id) => cacheEquipments[equipmentsMap[id]])
    ?.map((x) => cacheEquipments[equipmentsMap[x]]);

  const psvsData = psvs
    ?.filter((y) => y?.equipmentId)
    ?.map((x) => {
      const { jurisdictionNumber } = jurisdictionNumbersMap[x?.equipmentId] || {};
      const data = {
        ...x,
        category: x?.equipmentCategory,
        subtype: x?.equipmentSubType,
        jurisdictionNumber
      };

      const dataPoints = getDataPoints(
        { ...data, subtype: data?.equipmentSubType, jurisdictionNumber },
        equipmentDisplayFields,
        'equipment',
        picklistsMap
      );
      const category = picklistsMap[x?.equipmentCategory]?.value;
      const type = picklistsMap[x?.equipmentType]?.value;
      const subType = picklistsMap[x?.equipmentSubType]?.value;

      return {
        ...x,
        category,
        type,
        subType,
        dataPoints,
        verificationStatus: 'verify',
        isDetached: false,
        isReattach: false
      };
    });
  return {
    psvsData
  };
};

const getMoreInfoDataPoints1 = ({
  equipmentFormsUserCache,
  equipmentFormsUserCacheMap,
  equipmentDetails,
  picklists,
  equipmentCategoryTypeSubtype,
  equipmentAutoPopulateFields,
  inspection
}) => {
  const { picklistsMap = {} } = picklists;
  const { selectedFieldObjIds = [], moreFields = {} } = equipmentDetails;

  const otherCategories = [];
  if (_.isEmpty(moreFields)) {
    const dropdownMenuMapList = dropdownMenuMap({
      inspection
    });

    const { moreInfoFormFields } = getMoreInfoFormFields({
      equipmentFormsUserCache,
      dropdownMenuMapList,
      // isPSVVersion,
      selectedFieldObjIds: equipmentDetails?.selectedFieldObjIds || [],
      isNewEquipment: false
    });

    for (let field of moreInfoFormFields) {
      const sourceEntity = field.sourceEntity
        ? equipmentAutoPopulateFields[field.sourceEntity] || null
        : null;
      const sourceValue =
        sourceEntity && field.sourceProperty ? sourceEntity[field.sourceProperty] || null : null;

      const type = field?.type;

      if (
        sourceValue &&
        [
          'YN',
          'Date_Nullable',
          'Date',
          'Dropdown',
          'Dropdown_PL',
          'LongText',
          'Text',
          'PhoneNumber',
          'Numeric'
        ].includes(type) &&
        !field?.isFieldCheckedAndDisabled
      ) {
        let value =
          ['Date_Nullable', 'Date'].includes(type) && sourceValue
            ? moment(sourceValue).format('YYYY-MM-DD')
            : type === 'Dropdown_PL' && sourceValue
            ? picklistsMap[sourceValue]?.value || ''
            : sourceValue
            ? sourceValue
            : '';

        otherCategories.push({
          title: field?.label,
          value: value
        });
      } else if (
        (!_.isEmpty(sourceValue?.textBoxValue) || !_.isEmpty(sourceValue?.textBoxUnitValue)) &&
        ['Readings', 'Readings_PL'].includes(type) &&
        !field?.isFieldCheckedAndDisabled
      ) {
        const textBoxValue = sourceValue?.textBoxValue || '';
        const textBoxUnitValue = sourceValue?.textBoxUnitValue
          ? picklistsMap[sourceValue?.textBoxUnitValue]?.value || ''
          : '';
        let value = textBoxValue && textBoxUnitValue ? `${textBoxValue} ${textBoxUnitValue}` : '';

        otherCategories.push({
          title: field?.label,
          value: value
        });
      }
    }
  } else {
    Object.keys(moreFields)?.map((x) => {
      const id = Object.keys(moreFields[x])?.[0] || '';
      let value = moreFields[x][id] || '';

      const { displayName, dataType, picklistName } = equipmentFormsUserCacheMap[id];

      if (['Readings', 'Readings_PL'].includes(dataType)) {
        const textBoxValue = !_.isEmpty(value?.textBoxValue) ? value?.textBoxValue : '';

        const textBoxUnitValue = !_.isEmpty(value?.textBoxUnitValue)
          ? picklistsMap[value?.textBoxUnitValue]?.value || ''
          : '';
        value = textBoxValue && textBoxUnitValue ? `${textBoxValue} ${textBoxUnitValue}` : '';
      } else if (
        [
          'Date_Nullable',
          'Text',
          'Numeric',
          'Dropdown_PL',
          'LongText',
          'Date',
          'Dropdown',
          'PhoneNumber'
        ].indexOf(dataType)
      ) {
        if (picklistName === 'EquipmentCategoryTypeSubtypePickList' && value) {
          const equipmentCategoryKeys = getParentKeys(equipmentCategoryTypeSubtype, value);

          const equipmentCategory = picklistsMap[equipmentCategoryKeys[0] || '']?.value || '';
          const equipmentType = picklistsMap[equipmentCategoryKeys[1] || '']?.value || '';
          const equipmentSubType = picklistsMap[equipmentCategoryKeys[2] || '']?.value || '';

          value = `${equipmentCategory} ${equipmentType} ${equipmentSubType}`;
        } else {
          value =
            dataType === 'Dropdown_PL' && value
              ? picklistsMap[value]?.value || ''
              : value
              ? value
              : '';
        }
      }

      value &&
        otherCategories.push({
          title: displayName,
          value: value
        });
    });
  }

  return otherCategories;
};

const getParentKeys = (data, targetKey, parentKeys = []) => {
  for (const item of data) {
    if (item.key === targetKey) {
      parentKeys.push(item.key);
      break;
    } else if (item.child && item.child.length > 0) {
      parentKeys.push(item.key);
      getParentKeys(item.child, targetKey, parentKeys);
      if (parentKeys.includes(targetKey)) {
        break;
      } else {
        parentKeys.pop();
      }
    }
  }
  return parentKeys;
};
